import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PermissionHandlerService } from '../../services/permission-handler.service';
import { DataService } from 'src/app/services/data.service';
import { GlobalConstants } from 'src/app/services/global-constants';
import { TranslatorPipe } from '../../services/translator.pipe';
import { MessageService } from 'primeng/api';

interface ProductServiceQuestion {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'checkbox' | 'radio' | 'date' | 'file';
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  validation?: any;
  order: number;
}

interface ServiceField {
  _id?: string;
  label: string;
  type: string;
  data: string;
}

interface ProductServiceForm {
  _id?: string;
  name: string;
  type?: string | null;
  fields: ServiceField[];
  pricing: {
    amount: number | string; // Allow both number and string (e.g., "not decided yet")
    currency: string;
    includesVAT: boolean;
  };
  status: string;
  active: boolean;
  tempInactive?: boolean;
  signed?: {
    status: boolean;
    user_id: string | null;
    user_name: string | null;
    date: string;
  };
}

@Component({
  selector: 'app-product-services-form',
  templateUrl: './product-services-form.component.html',
  styleUrls: ['./product-services-form.component.scss']
})
export class ProductServicesFormComponent implements OnInit, OnChanges {
  @Input() productService: ProductServiceForm | null = null;
  @Input() isEditMode: boolean = false;
  @Output() formSubmit = new EventEmitter<ProductServiceForm>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() formSuccess = new EventEmitter<void>();

  

  productForm: FormGroup;
  isSubmitting: boolean = false;
  readonly SAUDI_RIYAL_SYMBOL = 'assets/pdf/graphics/Saudi_Riyal_Symbol-1.png';

  constructor(
    private fb: FormBuilder,
    public permissionService: PermissionHandlerService,
    private dataService: DataService,
    private messageService: MessageService
  ) {
    this.productForm = this.fb.group({
      name: ['', Validators.required],
      type: [null], // Payment method field
      pricing: this.fb.group({
        amount: ['', Validators.required], // Changed to accept text or number
        includesVAT: [true]
      }),
      // Static service fields
      serviceDescription: ['', Validators.required],
      serviceConditions: ['', Validators.required],
      targetAudience: [''],
      // serviceFees: [''],
      // singleQueryPrice: [''],
      serviceType: [''],
      servicePurpose: [''],
      otherInformation: [''],
      surveyLink: [''],
      surveyType: ['68ff1af35cd0be1a1ba880c9'],
      surveyName: [''],
      surveyDescription: [''],
    });
  }

  ngOnInit() {
    this.getSurveyTypes();
    this.getSurveys();
    this.loadProductTypes();

    if (this.productService) {
      this.loadProductService();
    } else {
      this.clearForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['productService']) {
      if (changes['productService'].currentValue) {
        this.loadProductService();
      } else {
        this.clearForm();
      }
    }
  }

  clearForm() {
    // Reset form to initial state
    this.productForm.reset({
      name: '',
      type: null,
      pricing: {
        amount: '',
        includesVAT: true
      },
      serviceDescription: '',
      serviceConditions: '',
      targetAudience: '',
      // serviceFees: '',
      // singleQueryPrice: '',
      serviceType: '',
      servicePurpose: '',
      otherInformation: '',
      surveyLink: '',
      surveyType: '',
      surveyName: '',
      surveyDescription: ''
    });
    this.selectedSurveyId = '';
  }

  loadProductService() {
    if (this.productService) {
      // Patch the main form values
      this.productForm.patchValue({
        name: this.productService.name,
        type: this.productService.type || null
      });
      
      // Patch the nested pricing form group
      this.productForm.get('pricing')?.patchValue({
        amount: this.productService.pricing.amount,
        includesVAT: this.productService.pricing.includesVAT
      });
      
      // Extract data from fields array and populate static fields
      if (this.productService.fields && this.productService.fields.length > 0) {
        const fieldsData: any = {};
        
        this.productService.fields.forEach(field => {
          switch (field.label) {
            case 'Service Description':
              fieldsData.serviceDescription = field.data;
              break;
            case 'Service Conditions and Requirements':
              fieldsData.serviceConditions = field.data;
              break;
            case 'Target Audience':
              fieldsData.targetAudience = field.data;
              break;
            // case 'Service Fees':
            //   fieldsData.serviceFees = field.data;
            //   break;
            // case 'Single Query Price':
            //   fieldsData.singleQueryPrice = field.data;
            //   break;
            case 'Service Type':
              fieldsData.serviceType = field.data;
              break;
            case 'Service Purpose':
              fieldsData.servicePurpose = field.data;
              break;
            case 'Other Information':
              fieldsData.otherInformation = field.data;
              break;
            case 'Survey Link':
              fieldsData.surveyLink = field.data;
              if (field.data) {
                const surveyId = this.getIdFromLink(field.data);
                if (surveyId) {
                  this.selectedSurveyId = surveyId;
                }
              }
              break;
            default:
              break;
          }
        });
        
        this.productForm.patchValue(fieldsData);
      }
      
      console.log('Form loaded with data:', {
        formValue: this.productForm.value,
        originalFields: this.productService.fields
      });
    }
  }

  onSubmit() {
    if (this.productForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      
      const formValue = this.productForm.value;
      const fields: ServiceField[] = [
        {
          _id: this.generateId(),
          label: 'Service Description',
          type: 'text',
          data: formValue.serviceDescription || ''
        },
        {
          _id: this.generateId(),
          label: 'Service Conditions and Requirements',
          type: 'textarea',
          data: formValue.serviceConditions || ''
        },
        {
          _id: this.generateId(),
          label: 'Target Audience',
          type: 'text',
          data: formValue.targetAudience || ''
        },
        // {
        //   _id: this.generateId(),
        //   label: 'Service Fees',
        //   type: 'text',
        //   data: formValue.serviceFees || ''
        // },
        // {
        //   _id: this.generateId(),
        //   label: 'Single Query Price',
        //   type: 'text',
        //   data: formValue.singleQueryPrice || ''
        // },
        {
          _id: this.generateId(),
          label: 'Service Type',
          type: 'text',
          data: formValue.serviceType || ''
        },
        {
          _id: this.generateId(),
          label: 'Service Purpose',
          type: 'text',
          data: formValue.servicePurpose || ''
        },
        {
          _id: this.generateId(),
          label: 'Other Information',
          type: 'textarea',
          data: formValue.otherInformation || ''
        },
        {
          _id: this.generateId(),
          label: 'Survey Link',
          type: 'text',
          data: formValue.surveyLink || '',
        }
      ];

      const formData: ProductServiceForm = {
        _id: this.productService?._id,
        name: formValue.name,
        type: formValue.type || null,
        fields: fields,
        pricing: {
          amount: formValue.pricing.amount,
          currency: 'SAR',
          includesVAT: formValue.pricing.includesVAT
        },
        status: 'ACTIVE',
        active: true,
        tempInactive: false
      };
      
      this.formSubmit.emit(formData);
    }
  }

  onCancel() {
    this.isSubmitting = false;
    this.formCancel.emit();
  }

  onFormSuccess() {
    this.isSubmitting = false;
    this.formSuccess.emit();
  }

  onFormError() {
    this.isSubmitting = false;
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  createNewSurvey: boolean = false;
  surveyTypes: any[] = [];
  allowedTags: string[] = this.permissionService.extractTags('SURVEY_TAGS', 'r')
  siteUrl: string = GlobalConstants.siteUrl;
  surveys: any[] = [];
  isSurveysLoading: boolean = false;
  selectedSurveyId: string = '';
  showAddQuestionView: boolean = false;
  currentQuestionSurveyId: string = '';
  currentQuestionBankId: string = '';
  productTypes: any[] = [];
  loadingProductTypes: boolean = false;


  getSurveyTypes() {
    if (this.allowedTags.length === 0) {
      this.allowedTags = ['MASTER_TAG'];
    }
    this.dataService.getData('survey-type/'+this.allowedTags.join(',')).then((res: any) => {
      if (res && res.data) {
        this.surveyTypes = res.data;
      }
    }).catch(err => {
      console.error('Error fetching survey types:', err);
    });
  }

  createSurvey() {
    let surveyDetails = {
      name: this.productForm.value.surveyName,
      description: this.productForm.value.surveyDescription,
      type: "68ff1af35cd0be1a1ba880c9",
      questions: [] as any[],
      active: true
    }
    this.dataService.postData('survey', surveyDetails).then((res: any) => {
      if (res && res.data) {
        this.productForm.patchValue({ surveyLink: this.siteUrl + 'sa/survey/attempt/' + res.data._id });
        this.createNewSurvey = false;
        this.messageService.add({ 
          severity: 'success', 
          summary: this.translate('Success'), 
          detail: this.translate('Survey created successfully. Survey link added to the service.') 
        });
      }
      this.isSubmitting= false
    }).catch(err => {
      this.isSubmitting= false
      console.error('Error creating survey:', err);
      this.messageService.add({ 
        severity: 'error', 
        summary: this.translate('Error'), 
        detail: this.translate('Error creating survey. Please try again.') 
      });
    });
  }

  checkLink() {
    let link = this.productForm.value.surveyLink;
    let id = this.getIdFromLink(link);

    if(id !== '') {
      this.dataService.getData('survey/id/'+id).then((res: any) => {
        if (res && res.data && res.data._id === id) {
          this.currentQuestionSurveyId = id;
          this.currentQuestionBankId = res.data.type?._id || res.data.type || '';
          this.showAddQuestionView = true;
        } else {
          this.messageService.add({ 
            severity: 'error', 
            summary: this.translate('Error'), 
            detail: this.translate('Invalid survey link. Please check and try again.') 
          });
        }
      }).catch(err => {
        console.error('Error fetching survey:', err);
        this.messageService.add({ 
          severity: 'error', 
          summary: this.translate('Error'), 
          detail: this.translate('Error validating survey link. Please check and try again.') 
        });
      });
    } else {
      this.messageService.add({ 
        severity: 'error', 
        summary: this.translate('Error'), 
        detail: this.translate('Invalid survey link format. Please check and try again.') 
      });
    }
  }

  closeAddQuestionView() {
    this.showAddQuestionView = false;
    this.currentQuestionSurveyId = '';
    this.currentQuestionBankId = '';
  }

  onQuestionsSaved() {
    this.closeAddQuestionView();
    this.messageService.add({ 
      severity: 'success', 
      summary: this.translate('Success'), 
      detail: this.translate('Questions saved successfully') 
    });
  }

  getIdFromLink(link: string): string {
    let id: string = '';

    if (link) {
      const match = link.match(/\/([^\/\?]+)(?:\?|$)/g);
      if (match && match.length > 0) {
        const lastSegment = match[match.length - 1];
        id = lastSegment.replace(/^\//, '').replace(/\?.*$/, '');
        console.log('Extracted ID:', id);
        // You can use the id as needed here
      } else {
        console.log('No ID found in link');
      }
    }

    return id;
  }

  translate(key: string): string {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  async getSurveys(): Promise<void> {
    this.isSurveysLoading = true;
    try {
      const productSurveyTypeId = '68ff1af35cd0be1a1ba880c9';
      const res = await this.dataService.getData('survey/MASTER_TAG?type=' + productSurveyTypeId);
      
      if (res && res.data && !res.error) {
        this.surveys = res.data
          .filter((item: any) => {
            const itemTypeId = typeof item.type === 'string' 
              ? item.type 
              : (item.type?._id || item.type);
            return itemTypeId === productSurveyTypeId;
          })
          .map((item: any) => {
            return {
              _id: item._id,
              name: item.name,
              type: item.type,
              comments: item.comments,
              surveyTitle: item.name,
              description: item.comments,
              status: item.status,
              trainingTypeId: item.trainingTypeId,
              questions: item.questions,
              startDate: item.startDate,
              endDate: item.endDate,
            };
          });
      }
    } catch (error) {
      console.error('Error loading surveys:', error);
      this.messageService.add({ 
        severity: 'error', 
        summary: this.translate('Error'), 
        detail: this.translate('Error loading surveys. Please try again.') 
      });
    } finally {
      this.isSurveysLoading = false;
    }
  }

  onSurveySelect(surveyId: string): void {
    if (surveyId) {
      const surveyUrl = this.siteUrl + 'sa/survey/attempt/' + surveyId;
      this.productForm.patchValue({ surveyLink: surveyUrl });
    }
  }

  async loadProductTypes(): Promise<void> {
    this.loadingProductTypes = true;
    try {
      const res = await this.dataService.getData('product-type');
      
      if (res && res.data && !res.error) {
        this.productTypes = res.data.filter((type: any) => type.active !== false);
      } else {
        this.productTypes = [];
      }
    } catch (error) {
      console.error('Error loading product types:', error);
      this.messageService.add({ 
        severity: 'error', 
        summary: this.translate('Error'), 
        detail: this.translate('Error loading product types. Please try again.') 
      });
      this.productTypes = [];
    } finally {
      this.loadingProductTypes = false;
    }
  }

}


